// src/components/HelperConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem,
    FormControl,
    InputLabel,
    Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../firebase";
import { ref, set, onValue, update, remove, push } from "firebase/database";

// Helper: convert onValue to Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function HelperConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [lokasiValue, setLokasiValue] = useState(""); // State for 'Lokasi'
    const [selectedKepalaKebun, setSelectedKepalaKebun] = useState(""); // State for 'id_kepala_kebun'
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentLokasiKey, setCurrentLokasiKey] = useState(null); // Key for editing

    const [kepalaKebunList, setKepalaKebunList] = useState([]); // List of Kepala Kebun for dropdown

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

    // Fetch data from Firebase
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(
            tbRef,
            (snapshot) => {
                const data = snapshot.val();
                if (snapshot.exists()) {
                    // Data is stored with 'lokasi' as the key
                    const list = Object.keys(data).map(key => ({
                        lokasi: key, // Use the key as 'lokasi'
                        id_kepala_kebun: data[key].id_kepala_kebun || '',
                        // Add other fields if necessary
                    }));
                    setDataList(list);
                } else {
                    setDataList([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error:", error);
                setSnackbar({
                    open: true,
                    message: `Failed to load ${title.toLowerCase()} data.`,
                    severity: "error",
                });
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [dbPath, title]);

    // Fetch Kepala Kebun list for dropdown
    useEffect(() => {
        const fetchKepalaKebun = async () => {
            const kepalaKebunRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun");
            const kepalaKebunSnapshot = await onValuePromise(kepalaKebunRef);
            const kepalaKebunData = kepalaKebunSnapshot.val();
            if (kepalaKebunData) {
                const list = Object.keys(kepalaKebunData).map(key => ({
                    id: key,
                    name: kepalaKebunData[key].nama_kepala_kebun
                }));
                setKepalaKebunList(list);
            }
        };
        fetchKepalaKebun();
    }, []);


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setLokasiValue("");
        setSelectedKepalaKebun("");
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setLokasiValue("");
        setSelectedKepalaKebun("");
        setIsEditing(false);
        setCurrentLokasiKey(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        if (!lokasiValue.trim()) {
            setSnackbar({
                open: true,
                message: "Lokasi cannot be empty.",
                severity: "warning",
            });
            return;
        }

        const normalizedLokasiValue = lokasiValue.toLowerCase().trim();

        // Check for duplicate 'lokasi' (which is the key)
        const isDuplicate = dataList.some(item =>
            item.lokasi?.toLowerCase().trim() === normalizedLokasiValue &&
            item.lokasi !== currentLokasiKey // Allow editing the current item
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: `Location "${lokasiValue}" already exists.`,
                severity: "error",
            });
            return;
        }

        try {
            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentLokasiKey}`);
                const updatedData = {
                    lokasi: lokasiValue, // Update the 'lokasi' field if the key changes, though the key itself is 'lokasi'
                    id_kepala_kebun: selectedKepalaKebun,
                };
                await update(itemRef, updatedData);
                setSnackbar({
                    open: true,
                    message: "Location data successfully updated.",
                    severity: "success",
                });
            } else {
                const newRef = ref(database, `${dbPath}/${lokasiValue}`); // Use lokasiValue as the key
                const data = {
                    lokasi: lokasiValue,
                    id_kepala_kebun: selectedKepalaKebun,
                };
                await set(newRef, data); // Use set to write directly to the specified key
                setSnackbar({
                    open: true,
                    message: "Location data successfully saved.",
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Failed to save/update:", error);
            setSnackbar({
                open: true,
                message: `Failed to save/update ${title.toLowerCase()} data.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0]; // Assuming first sheet
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);
                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                let importedCount = 0;
                let ignoredCount = 0;

                // Create a map for Kepala Kebun lookup
                const kepalaKebunMap = {};
                kepalaKebunList.forEach(kk => kepalaKebunMap[kk.name.toLowerCase()] = kk.id);

                for (const item of jsonData) {
                    const lokasi = item["Lokasi"];
                    const wilayahPlantCare = item["Wilayah Plant Care"];

                    if (!lokasi) {
                        ignoredCount++;
                        continue;
                    }

                    const normalizedLokasi = String(lokasi).trim();

                    // Check if the location already exists as a key in Firebase
                    if (existingData[normalizedLokasi]) {
                        ignoredCount++;
                        continue;
                    }

                    let kepalaKebunId = '';
                    if (wilayahPlantCare) {
                        kepalaKebunId = kepalaKebunMap[String(wilayahPlantCare).toLowerCase()] || '';
                    }

                    const dataToSave = {
                        lokasi: normalizedLokasi,
                        id_kepala_kebun: kepalaKebunId,
                    };

                    await set(ref(database, `${dbPath}/${normalizedLokasi}`), dataToSave);
                    importedCount++;
                }

                setSnackbar({
                    open: true,
                    message: `Successfully imported ${importedCount} new data. ${ignoredCount} existing/invalid data ignored.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Failed to import Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to import data from Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };

        reader.readAsArrayBuffer(file);
    };


    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "No data to export.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            row["Lokasi"] = item.lokasi;
            row["Wilayah Plant Care"] = kepalaKebunList.find(kk => kk.id === item.id_kepala_kebun)?.name || item.id_kepala_kebun || 'N/A';
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
        setSnackbar({
            open: true,
            message: "Data successfully exported to Excel.",
            severity: "success",
        });
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentLokasiKey(row.lokasi); // Set the current lokasi as key
        setLokasiValue(row.lokasi);
        setSelectedKepalaKebun(row.id_kepala_kebun || '');
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;

        try {
            const idToDelete = selectedRow.lokasi; // Use 'lokasi' as the key to delete
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `Data ${selectedRow.lokasi} successfully deleted.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Failed to delete:", error);
            setSnackbar({
                open: true,
                message: `Failed to delete ${title.toLowerCase()} data.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            <TableCell>Lokasi</TableCell>
                            <TableCell>Wilayah Plant Care</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.lokasi}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row.lokasi}</TableCell>
                                        <TableCell>
                                            {kepalaKebunList.find(kk => kk.id === row.id_kepala_kebun)?.name || 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    No data available.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Delete
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? "Edit Plant Care" : "Add Plant Care"}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Lokasi"
                        type="text"
                        fullWidth
                        value={lokasiValue}
                        onChange={(e) => setLokasiValue(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            readOnly: isEditing, // Lokasi is read-only when editing since it's the key
                        }}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Kepala Kebun</InputLabel>
                        <Select
                            value={selectedKepalaKebun}
                            onChange={(e) => setSelectedKepalaKebun(e.target.value)}
                            label="Kepala Kebun"
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {kepalaKebunList.map((kk) => (
                                <MenuItem key={kk.id} value={kk.id}>{kk.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete location "{selectedRow?.lokasi}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default HelperConfig;
